import api from '@/api';

class Property {
  /**
   * @returns {AxiosPromise<any>}
   */
  static find(propertyId) {
    return api.get(`/properties/${propertyId}`);
  }

  /**
   * Retrieve all items
   * @param opts
   * @returns {Promise<unknown>}
   */
  static async all(opts = {}) {
    const params = opts.params ? opts.params : {};
    params.limit = params.limit || 500;
    params.page = 1;

    // Get the first page to determine total pages
    const firstPageResponse = await this.list({ params, endpoint: opts.endpoint });
    const { pagination } = firstPageResponse.data.meta;
    const { total, total_pages } = pagination;

    let allData = [...firstPageResponse.data.data];

    if (total_pages > 1) {
      const pagePromises = [];
      for (let page = 2; page <= total_pages; page++) {
        const pageParams = { ...params, page };
        pagePromises.push(this.list({ params: pageParams, endpoint: opts.endpoint }));
      }

      // Wait for all promises to resolve
      const responses = await Promise.all(pagePromises);

      responses.forEach(response => {
        allData = [...allData, ...response.data.data];
      });
    }

    return {
      data: {
        data: allData,
        meta: {
          total,
        },
      },
    };
  }

  /**
   * Index / Get listing of properties
   * @returns {*}
   */
  static list(params) {
    return api.get('/properties', params);
  }

  /**
   * Add a property
   * @param params
   * @returns {*}
   */
  static add(params) {
    return api.post('/properties', params);
  }

  /**
   * Update a property
   * @param propertyId
   * @param params
   * @returns {*}
   */
  static update(propertyId, params) {
    return api.put(`/properties/${propertyId}`, params);
  }

  /**
   * Update a batch of properties' client designation
   * @param propertyIds
   * @param clientPropertyDesignation
   * @returns {AxiosPromise<any> | IDBRequest<IDBValidKey> | Promise<void>}
   */
  static updateClientDesignation({ propertyIds, clientPropertyDesignation }) {
    return api.put('/properties/client-designations', { propertyIds, clientPropertyDesignation });
  }
  /**
   * Update a batch of properties' property designation
   * @param propertyIds
   * @param propertyStatusId
   * @returns {AxiosPromise<any> | IDBRequest<IDBValidKey> | Promise<void>}
   */
  static updateStatus({ propertyIds, propertyStatusId }) {
    return api.put('/properties/statuses', { propertyIds, propertyStatusId });
  }
  /**
   * Retrieve a list of property designations
   * @returns {*}
   */
  static getActions(propertyId, params) {
    return api.get(`/properties/${propertyId}/actions/riskhub`, { params });
  }

  /**
   * Retrieve a list of property designations
   * @returns {*}
   */
  static getClientActions(propertyId, params) {
    return api.get(`/properties/${propertyId}/actions/client`, { params });
  }


  /**
   * Gets counts for properties key'd by status
   *
   * @returns {AxiosPromise<any>}
   */
  static getCounts() {
    return api.get('/properties/counts');
  }

  /**
   * Gets extended counts
   *
   * @param complianceTypeId
   * @param interval
   * @param propertyTenure
   * @param {string} dueDateType
   * @returns {Promise<AxiosResponse<T>>}
   */
  static getExtendedCounts(complianceTypeId, interval, propertyTenure, dueDateType) {
    return api.get('/properties/counts/extended', {
      params: {
        complianceTypeId,
        interval,
        propertyTenure,
        dueDateType,
      },
    });
  }

  /**
   * Get new and renewals properties
   * @returns {*}
   */
  static getNewAndRenewals(complianceTypeId, toDate, fromDate, propertyTenure, dueDateType) {
    const params = {
      complianceTypeId,
      propertyTenure: propertyTenure || 'All Tenures',
      dueDateType: dueDateType || 'programDueDate',
    };

    if(fromDate) {
      params.fromDate = fromDate;
    }
    if(toDate) {
      params.toDate = toDate;
    }

    return api.get('/properties/new-and-renewals/', { params });
  }

  /**
   * Get new and renewals properties
   * @returns {*}
   */
  static exportNewAndRenewals(complianceTypeId, toDate, fromDate, propertyTenure, dueDateType) {
    const params = {
      complianceTypeId,
        propertyTenure: propertyTenure || 'All Tenures',
        dueDateType: dueDateType || 'programDueDate'
    }

    if(fromDate) {
      params.fromDate = fromDate;
    }
    if(toDate) {
      params.toDate = toDate;
    }

    return api.get('/properties/new-and-renewals/export', { params });
  }
}

export default Property;
